// The Access tab of the athlete surface (see `features/athlete-access.md`): who
// else can see this profile, and what the caller may do about it.
//
// Who may do what is decided by the server (`canRevokeAccess`), and mirrored here
// only to decide what to render:
//
//   * a guardian may withdraw a child's login, step down from their own grant, and
//     give the child their own login (at any age — the guardian's call, recorded
//     against their account);
//   * an adult athlete may remove a guardian — the point at which oversight
//     becomes theirs to end;
//   * a minor athlete may not remove their guardian, and is told so rather than
//     shown a button that would fail.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import type {
  AthleteAccessPanel,
  AthleteAccessView,
  AthleteLinkView,
} from '../../domain';
import { isValidEmail } from '../../domain';
import {
  fetchMyAthleteAccess,
  grantChildAccess,
  revokeMyAccess,
} from '../../data/apiClient';
import { useTranslation } from '../../state/I18nContext';

interface MyAccessSectionProps {
  link: AthleteLinkView;
  /** Called after any change, so the app can refresh its own access picture. */
  onChanged: () => void;
}

export function MyAccessSection({ link, onChanged }: MyAccessSectionProps) {
  const { t, localeCode } = useTranslation();
  const [panel, setPanel] = useState<AthleteAccessPanel | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<AthleteAccessView | null>(null);

  const isGuardian = link.relation === 'GUARDIAN';
  // Mirrors `canRevokeAccess`: a guardian may withdraw a child's login or step
  // down from their OWN grant — never a peer guardian's, since guardians are
  // peers; an adult athlete may remove any guardian; a minor athlete, nothing.
  const canRevoke = useCallback(
    (grant: AthleteAccessView): boolean => {
      if (grant.revokedAt !== null) return false;
      if (isGuardian) return grant.relation === 'SELF' || grant.id === link.accessId;
      return grant.relation === 'GUARDIAN' && !link.isMinor;
    },
    [isGuardian, link.accessId, link.isMinor],
  );

  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setPanel(await fetchMyAthleteAccess(link.personalNumber));
      setError(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : tRef.current('athlete.access.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [link.personalNumber]);

  useEffect(() => {
    void load();
  }, [load]);

  const dateFormat = useMemo(
    () => new Intl.DateTimeFormat(localeCode, { dateStyle: 'medium' }),
    [localeCode],
  );

  const liveGrants = useMemo(
    () => (panel?.grants ?? []).filter((grant) => grant.revokedAt === null),
    [panel],
  );

  const submitInvite = async () => {
    if (!isValidEmail(email)) {
      setEmailError(t('athlete.access.grant.emailInvalid'));
      return;
    }
    setBusy(true);
    setError(null);
    setEmailError(null);
    try {
      await grantChildAccess(link.personalNumber, email.trim());
      setInviteOpen(false);
      setNotice(t('athlete.access.grant.sent', { email: email.trim() }));
      setEmail('');
      await load();
      onChanged();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('athlete.access.grant.failed'));
    } finally {
      setBusy(false);
    }
  };

  const confirmRevoke = async () => {
    if (!revoking) return;
    setBusy(true);
    setError(null);
    try {
      await revokeMyAccess(revoking.id);
      setRevoking(null);
      await load();
      onChanged();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('athlete.access.revoke.failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {t('athlete.access.title')}
          </Typography>
          {isGuardian && (
            <Button
              size="small"
              startIcon={<PersonAddIcon />}
              disabled={busy}
              onClick={() => {
                setEmailError(null);
                setInviteOpen(true);
              }}
            >
              {t('athlete.access.grant.action')}
            </Button>
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t(
            isGuardian
              ? 'athlete.access.hintGuardian'
              : link.isMinor
                ? 'athlete.access.hintMinor'
                : 'athlete.access.hintAdult',
            { name: link.fullName },
          )}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {notice && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
            {notice}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Stack divider={<Divider flexItem />}>
            {liveGrants.map((grant) => (
              <Stack
                key={grant.id}
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ py: 1.5 }}
              >
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" noWrap title={grant.email}>
                    {grant.email}
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                    <Chip
                      size="small"
                      color={grant.relation === 'GUARDIAN' ? 'secondary' : 'primary'}
                      variant="outlined"
                      label={t(`athlete.access.holder.${grant.relation}`)}
                    />
                    {grant.id === link.accessId && (
                      <Chip size="small" label={t('athlete.access.holder.you')} />
                    )}
                    <Chip
                      size="small"
                      variant="outlined"
                      label={t('athlete.access.since', {
                        date: dateFormat.format(new Date(grant.createdAt)),
                      })}
                    />
                  </Stack>
                </Box>
                {canRevoke(grant) && (
                  <Button
                    size="small"
                    color="error"
                    disabled={busy}
                    onClick={() => setRevoking(grant)}
                  >
                    {t('athlete.access.revoke.action')}
                  </Button>
                )}
              </Stack>
            ))}
            {(panel?.pending ?? []).map((invite) => (
              <Stack
                key={invite.id}
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ py: 1.5 }}
              >
                <MailOutlineIcon color="disabled" fontSize="small" />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" noWrap title={invite.email}>
                    {invite.email}
                  </Typography>
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    sx={{ mt: 0.5 }}
                    label={t('athlete.access.pendingUntil', {
                      date: dateFormat.format(new Date(invite.expiresAt)),
                    })}
                  />
                </Box>
              </Stack>
            ))}
            {liveGrants.length === 0 && (panel?.pending ?? []).length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                {t('athlete.access.empty')}
              </Typography>
            )}
          </Stack>
        )}
      </Paper>

      {/* Give the child their own login. */}
      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t('athlete.access.grant.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('athlete.access.grant.body', { name: link.fullName })}
          </DialogContentText>
          <TextField
            fullWidth
            autoFocus
            type="email"
            label={t('athlete.access.grant.email')}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setEmailError(null);
            }}
            error={emailError !== null}
            helperText={emailError ?? t('athlete.access.grant.emailHelp')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteOpen(false)} disabled={busy}>
            {t('athlete.access.cancel')}
          </Button>
          <Button variant="contained" onClick={() => void submitInvite()} disabled={busy}>
            {t('athlete.access.grant.send')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke confirmation. */}
      <Dialog open={revoking !== null} onClose={() => setRevoking(null)}>
        <DialogTitle>{t('athlete.access.revoke.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('athlete.access.revoke.body', {
              email: revoking?.email ?? '',
              name: link.fullName,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevoking(null)} disabled={busy}>
            {t('athlete.access.cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void confirmRevoke()}
            disabled={busy}
          >
            {t('athlete.access.revoke.action')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
